import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

const AVANI_FEATURES = [
    {
        badge: 'AUTOMATIC BOT JOINING',
        title: 'AVANI AI Meeting Notetaker & Analyzer',
        desc: 'Dispatches automatically to Google Meet, Zoom & Teams. Listens, records audio, and identifies active speakers seamlessly.',
        highlights: [
            'Auto-joins Google Meet, Zoom & Teams calls in under 5 seconds',
            'Real-time speaker diarization and participant timeline tracking',
            'Secure cloud audio recording with AVANI AI branding avatar'
        ],
        simulation: 'AVANI AI: Joined room as Notetaker Assistant. Recording active.',
        badgeColor: 'bg-indigo-500/20 text-indigo-300 border-indigo-400/30'
    },
    {
        badge: 'NEURAL ACCURACY',
        title: '100% Accurate Minutes of Meeting (MoM)',
        desc: 'Powered by AVANI Neural Audio Engine and AI synthesis. Instant MoM, Key Discussion Topics, and Decisions Made.',
        highlights: [
            'AVANI Neural Audio Engine transcribes Hindi, English & Hinglish',
            'Automated Executive Summary & Key Discussion Topics',
            'Clear Decisions Made section & Key Insights'
        ],
        simulation: 'AVANI Neural AI: 3.2MB audio processed. Full MoM generated!',
        badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-400/30'
    },
    {
        badge: 'TASK DELEGATION',
        title: 'One-Click Task Conversion & Delegation',
        desc: 'Automatically extracts follow-up action items from meeting discussions and converts them directly into assigned Business OS Tasks.',
        highlights: [
            'Auto-extracts action points and follow-ups from live audio',
            'Pre-filled assignee names, priority levels, and suggested due dates',
            '1-Click "Delegate Task" sync straight to Business OS Tasks module'
        ],
        simulation: 'Task Delegated: "Send proposal to client by tomorrow 5 PM"',
        badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-400/30'
    },
    {
        badge: 'EXECUTIVE ANALYTICS',
        title: 'Outcome Tracking & Speaker Timelines',
        desc: 'Track meeting status (Successful, Follow-up Needed, Unsure) with complete transcript history and speaker timelines.',
        highlights: [
            'Automated outcome classification (Successful, Follow-up, Unsure)',
            'Line-by-line speaker timelines with exact timestamps',
            'Instant MoM sharing with team attendees and managers'
        ],
        simulation: 'Analytics: Meeting classified as "Successful" with 2 action items.',
        badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30'
    }
];

const AvaniAiBanner = () => {
    const { user } = useAuth();
    const [hidden, setHidden] = useState(true);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        if (!user) {
            setHidden(true);
            return;
        }
        const storedPref = localStorage.getItem('hide_avani_ai_banner');
        if (storedPref === 'true') {
            setHidden(true);
        } else {
            setHidden(false);
        }
    }, [user]);

    // Auto rotate slides every 6 seconds
    useEffect(() => {
        if (hidden) return;
        const timer = setInterval(() => {
            setCurrentSlide(prev => (prev + 1) % AVANI_FEATURES.length);
        }, 6000);
        return () => clearInterval(timer);
    }, [hidden]);

    const handleDismiss = () => {
        if (dontShowAgain) {
            localStorage.setItem('hide_avani_ai_banner', 'true');
        }
        setHidden(true);
    };

    const [isMuted, setIsMuted] = useState(true);
    const videoRef = React.useRef(null);

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    if (hidden || !user) return null;

    const feature = AVANI_FEATURES[currentSlide];
    const meetingsLink = user?.role === 'employee' ? '/employee/meetings' : '/hr/meetings';

    return (
        <div className="w-full mb-6 animate-fade-in">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-950 via-indigo-950 to-purple-950 border border-purple-500/30 p-4 sm:p-6 shadow-2xl backdrop-blur-2xl text-white">
                {/* Background Ambient Glows */}
                <div className="absolute -top-24 -left-24 w-72 h-72 bg-purple-600/25 rounded-full blur-3xl pointer-events-none animate-pulse" />
                <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />

                {/* Top Header Bar: Highlighted Title + Close */}
                <div className="flex items-center justify-between gap-4 mb-4 pb-3 border-b border-white/10">
                    <div className="flex items-center gap-2.5">
                        <span className="relative flex h-3.5 w-3.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-amber-500"></span>
                        </span>
                        <span className="text-xs sm:text-sm font-black uppercase tracking-widest bg-gradient-to-r from-amber-300 via-amber-100 to-purple-300 bg-clip-text text-transparent flex items-center gap-2">
                            AVANI AI MEETINGS ANALYZER & NOTETAKER
                        </span>
                    </div>

                    <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-300 hover:text-white cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={dontShowAgain}
                                onChange={(e) => setDontShowAgain(e.target.checked)}
                                className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-900 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer"
                            />
                            <span>Don't show again</span>
                        </label>

                        <button
                            onClick={handleDismiss}
                            className="p-1 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white transition-colors"
                            title="Dismiss Banner"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Main Grid: Left Feature Details + Right Autoplay Video */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                    {/* LEFT COLUMN: Feature Highlights Carousel */}
                    <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
                        <div key={currentSlide} className="animate-fade-in space-y-3">
                            {/* Slide Badge & Quick Action */}
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10.5px] font-extrabold tracking-wider border ${feature.badgeColor}`}>
                                    <svg className="w-3.5 h-3.5 text-current" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    <span>{feature.badge}</span>
                                </div>

                                <Link
                                    to={meetingsLink}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-black bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-gray-950 shadow-md hover:scale-105 transition-all"
                                >
                                    <span>Go to Meetings</span>
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </Link>
                            </div>

                            {/* Feature Title */}
                            <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-snug">
                                {feature.title}
                            </h3>

                            {/* Feature Description */}
                            <p className="text-xs sm:text-sm text-gray-300 font-medium leading-relaxed">
                                {feature.desc}
                            </p>

                            {/* Feature Bullet Highlights */}
                            <div className="space-y-1.5 pt-1">
                                {feature.highlights.map((h, i) => (
                                    <div key={i} className="flex items-center gap-2.5 text-xs text-gray-200 font-semibold bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-amber-400/20 text-amber-300 flex items-center justify-center text-[10px] font-bold">✓</span>
                                        <span>{h}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Interactive Live Chat Simulation Card */}
                            <div className="bg-black/50 border border-purple-400/30 rounded-xl p-2.5 flex items-center gap-2.5 font-mono text-[11px] text-amber-300 shadow-inner">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping flex-shrink-0" />
                                <span className="truncate">{feature.simulation}</span>
                            </div>
                        </div>

                        {/* Carousel Navigation Bar */}
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-white/10">
                            {/* Slide Dots */}
                            <div className="flex items-center gap-1.5">
                                {AVANI_FEATURES.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentSlide(idx)}
                                        className={`h-2.5 rounded-full transition-all duration-300 ${
                                            currentSlide === idx ? 'w-8 bg-gradient-to-r from-amber-400 via-amber-300 to-purple-400' : 'w-2.5 bg-white/20 hover:bg-white/40'
                                        }`}
                                        title={`Go to slide ${idx + 1}`}
                                    />
                                ))}
                            </div>

                            {/* Controls */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentSlide(prev => (prev === 0 ? AVANI_FEATURES.length - 1 : prev - 1))}
                                    className="px-3 py-1 text-xs font-bold rounded-lg bg-white/10 hover:bg-white/20 transition-all active:scale-95"
                                >
                                    ‹ Prev
                                </button>
                                <button
                                    onClick={() => setCurrentSlide(prev => (prev + 1) % AVANI_FEATURES.length)}
                                    className="px-3 py-1 text-xs font-bold rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 transition-all active:scale-95 shadow-md"
                                >
                                    Next ›
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Autoplay Video Player */}
                    <div className="lg:col-span-5 relative flex flex-col justify-center">
                        <div className="relative overflow-hidden rounded-xl border border-purple-400/40 shadow-2xl bg-black aspect-video group">
                            <video
                                ref={videoRef}
                                src="/ai.mp4"
                                autoPlay
                                muted={isMuted}
                                loop
                                playsInline
                                className="w-full h-full object-cover"
                            />
                            {/* Live Demo Overlay Badge */}
                            <div className="absolute top-2.5 left-2.5 bg-black/70 backdrop-blur-md px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider text-amber-300 border border-amber-400/40 flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                                Live Demo · AVANI AI
                            </div>

                            {/* Mute / Unmute Toggle Button */}
                            <button
                                onClick={toggleMute}
                                className="absolute bottom-2.5 right-2.5 bg-black/75 hover:bg-black/90 backdrop-blur-md px-2.5 py-1 rounded-md text-[10.5px] font-extrabold text-white border border-white/20 flex items-center gap-1.5 shadow-lg transition-all active:scale-95 cursor-pointer z-10"
                                title={isMuted ? 'Click to Unmute Sound' : 'Click to Mute Sound'}
                            >
                                {isMuted ? (
                                    <>
                                        <svg className="w-3.5 h-3.5 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                        </svg>
                                        <span>Tap to Unmute</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-3.5 h-3.5 text-emerald-400 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                        </svg>
                                        <span>Sound On</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AvaniAiBanner;
